/* ==========================================================================
   Video Shop — shoppable video reels for Minimog

   Performance model:
   - Nothing is fetched until needed. Videos ship with preload="none" and a
     data-vs-src; the real src is assigned only when the element nears the
     viewport, so the section costs no bandwidth during first paint.
   - Cards autoplay muted on scroll, started ONE AT A TIME via a queue, with at
     most MAX_CONCURRENT playing so mobile decoders don't thrash.
   - Offscreen videos are paused, and unloaded once far away.
   - Drag is rAF-batched; nothing writes layout inside a pointermove.
   - The lightbox is a native <dialog> in the top layer, so it covers sticky
     headers and app embeds without scanning the DOM for fixed elements.
   ========================================================================== */

(function () {
  'use strict';

  var MAX_CONCURRENT = 3;       // simultaneously playing card videos
  var NEAR_MARGIN    = '600px'; // begin loading this far outside the viewport
  var PLAY_RATIO     = 0.35;    // how much of a card must be visible to play
  var START_TIMEOUT  = 3000;    // give up waiting on one video, move to the next
  var DESKTOP_MQ     = '(min-width: 1024px)';

  // Add ?vsdebug=1 to the URL to log why playback did or didn't start.
  var DEBUG = /[?&]vsdebug=1/.test(window.location.search);

  /* --------------------------- environment ------------------------------ */

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isDesktop() {
    return window.matchMedia && window.matchMedia(DESKTOP_MQ).matches;
  }

  function saveData() {
    var c = navigator.connection;
    if (!c) return false;
    if (c.saveData) return true;
    return /(^|-)2g$/.test(c.effectiveType || '');
  }

  /* ---------------------------- analytics ------------------------------- */

  function track(eventName, params) {
    params = params || {};
    try { if (window.fbq) window.fbq('trackCustom', eventName, params); } catch (e) {}
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, params));
    } catch (e) {}
  }

  /* ------------------------------ storage -------------------------------- */

  function readLike(blockId) {
    try { return localStorage.getItem('video-shop-like-' + blockId) === '1'; }
    catch (e) { return false; }
  }

  function writeLike(blockId, liked) {
    try {
      if (liked) localStorage.setItem('video-shop-like-' + blockId, '1');
      else localStorage.removeItem('video-shop-like-' + blockId);
    } catch (e) {}
  }

  /* --------------------------- media loading ----------------------------- */

  function loadMedia(video) {
    if (!video || video.dataset.vsLoaded === '1') return;
    var src = video.getAttribute('data-vs-src');
    if (!src) return;
    video.src = src;
    video.dataset.vsLoaded = '1';
  }

  function unloadMedia(video) {
    if (!video || video.dataset.vsLoaded !== '1') return;
    video.pause();
    video.removeAttribute('src');
    video.load();            // release the decoder + buffered data
    delete video.dataset.vsLoaded;
  }

  function safePlay(video) { attemptPlay(video, null); }

  /* play() can reject for two different reasons: the autoplay policy, or the
     media not being ready yet (preload="none" buffers nothing until asked).
     Handle both instead of giving up on the first rejection. */
  function attemptPlay(video, onPlaying) {
    if (!video) return;

    function settled() { if (onPlaying) onPlaying(); }

    function tryOnce(muteFirst) {
      if (muteFirst) {
        video.muted = true;
        video.setAttribute('muted', '');
      }
      var p;
      try { p = video.play(); } catch (e) { return; }
      if (!p || !p.then) { settled(); return; }

      p.then(settled).catch(function () {
        if (!muteFirst) { tryOnce(true); return; }   // policy rejection -> muted retry
        video.addEventListener('canplay', function once() {
          video.removeEventListener('canplay', once);
          var again = video.play();
          if (again && again.then) again.then(settled).catch(function (err) {
            if (DEBUG) console.warn('[video-shop] play failed', video.currentSrc, err);
          });
        }, { once: true });
      });
    }

    tryOnce(video.muted);
  }

  /* ============================== add to cart ============================= */

  /* Uses Minimog's own contract: request the cart sections the theme wants
     re-rendered, emit cart-update so <m-cart-drawer> refreshes, then open it. */
  function addToCart(btn) {
    var variantId = btn.getAttribute('data-variant-id');
    var complete = btn.getAttribute('data-complete') === '1';
    var productUrl = btn.getAttribute('data-product-url');

    // Only add in place once the variant is fully resolved — a single-variant
    // product, or a colour picked from the swatches when colour is the only
    // option. Otherwise a size is still needed, so hand off to the product page
    // (deep-linked to the chosen colour variant when we have one).
    if (!complete || !variantId) {
      if (productUrl) window.location.href = productUrl;
      return;
    }

    var settings = window.MinimogSettings || {};
    var routes = settings.routes || {};
    var addUrl = routes.cart_add_url || '/cart/add';

    // Theme components push the section names they need refreshed.
    var sections = [];
    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', {
        bubbles: true, detail: { sections: sections }
      }));
    } catch (e) {}

    var label = btn.querySelector('[data-atc-label]');
    var original = label ? label.textContent : '';
    btn.classList.add('is-loading');
    btn.disabled = true;

    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sections,
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res.status) throw new Error(res.description || res.message || 'Add to cart failed');

        track('video_shop_add_to_cart', { variant_id: variantId });

        if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
          window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
            cart: res, productVariantId: variantId
          });
        }
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: res } }));

        // The cart drawer is z-index based, so it would render BEHIND a
        // top-layer <dialog>. Close the lightbox first, then open the drawer.
        var section = btn.closest('[data-vs-root]');
        var inst = section && instances.get(section);
        if (inst && inst.lightbox && inst.lightbox.close) inst.lightbox.close();

        var drawer = document.querySelector('m-cart-drawer');
        if (drawer && settings.enable_cart_drawer && typeof drawer.open === 'function') {
          drawer.open();
        } else if (window.MinimogTheme && window.MinimogTheme.Notification) {
          window.MinimogTheme.Notification.show({
            target: document.body, method: 'appendChild', type: 'success',
            message: (window.MinimogStrings && window.MinimogStrings.itemAdded) || 'Added to cart',
            last: 3000, sticky: true
          });
        }
      })
      .catch(function (err) {
        if (DEBUG) console.warn('[video-shop] add to cart failed', err);
        // Never strand the shopper: fall back to the product page.
        if (productUrl) window.location.href = productUrl;
      })
      .then(function () {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        if (label) label.textContent = original;
      });
  }

  /* ===================== scroll-triggered card autoplay ================== */

  function initAutoplay(root) {
    // Reduced-motion deliberately does NOT disable playback: a muted looping
    // product video is content, not decoration, and Windows' "animation
    // effects" toggle reports reduce. CSS still drops decorative animation.
    var enabled = root.hasAttribute('data-vs-autoplay') &&
                  !saveData() &&
                  'IntersectionObserver' in window;

    if (DEBUG) {
      console.log('[video-shop] autoplay enabled:', enabled, {
        attr: root.hasAttribute('data-vs-autoplay'),
        saveData: saveData(),
        reducedMotion: prefersReducedMotion(),
        io: 'IntersectionObserver' in window
      });
    }

    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-video-card]'));
    if (!cards.length) return null;

    var loader = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target.querySelector('[data-vs-video]');
        if (!video) return;
        if (entry.isIntersecting) loadMedia(video);
        else unloadMedia(entry.target.classList.contains('is-playing') ? null : video);
      });
    }, { rootMargin: NEAR_MARGIN, threshold: 0 });

    cards.forEach(function (card) { loader.observe(card); });

    if (!enabled) return { destroy: function () { loader.disconnect(); } };

    var playing  = [];    // currently playing, oldest first
    var queue    = [];    // waiting to start, in DOM order
    var visible  = [];    // currently past PLAY_RATIO
    var starting = false; // serialises starts: one video begins at a time

    function stop(card) {
      var i = playing.indexOf(card);
      if (i !== -1) playing.splice(i, 1);
      var q = queue.indexOf(card);
      if (q !== -1) queue.splice(q, 1);
      var video = card.querySelector('[data-vs-video]');
      if (video) video.pause();
      card.classList.remove('is-playing');
    }

    /* One at a time, in DOM order: the first card is fetched and playing before
       the second is requested. Starting them together made every request
       compete for the same bandwidth, so none started promptly. */
    function enqueue(card) {
      if (playing.indexOf(card) !== -1 || queue.indexOf(card) !== -1) return;
      queue.push(card);
      queue.sort(function (a, b) { return cards.indexOf(a) - cards.indexOf(b); });
    }

    function pump() {
      if (starting || !queue.length || playing.length >= MAX_CONCURRENT) return;

      var card = queue.shift();
      if (visible.indexOf(card) === -1) { pump(); return; }  // scrolled away while waiting

      var video = card.querySelector('[data-vs-video]');
      if (!video) { pump(); return; }

      starting = true;

      var released = false;
      var timer;
      function release() {
        if (released) return;
        released = true;
        clearTimeout(timer);
        starting = false;
        pump();                     // hand the slot to the next card
      }
      timer = setTimeout(function () {
        if (DEBUG) console.warn('[video-shop] start timed out', card.dataset.blockId);
        release();
      }, START_TIMEOUT);

      playing.push(card);
      video.muted = true;
      video.setAttribute('muted', '');
      video.playsInline = true;
      loadMedia(video);

      if (DEBUG) console.log('[video-shop] starting', card.dataset.blockId);

      attemptPlay(video, function () {
        card.classList.add('is-playing');
        release();
      });
    }

    // Reveal on whichever signal lands first, so a video can never play
    // invisibly behind its own poster.
    cards.forEach(function (card) {
      var video = card.querySelector('[data-vs-video]');
      if (!video) return;
      ['playing', 'loadeddata', 'timeupdate'].forEach(function (evt) {
        video.addEventListener(evt, function () {
          if (!video.paused) card.classList.add('is-playing');
        });
      });
    });

    var player = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var card = entry.target;
        var shown = entry.isIntersecting && entry.intersectionRatio >= PLAY_RATIO;
        var at = visible.indexOf(card);

        if (shown && at === -1) visible.push(card);
        if (!shown && at !== -1) visible.splice(at, 1);

        if (DEBUG) {
          console.log('[video-shop]', card.dataset.blockId,
                      'ratio', entry.intersectionRatio.toFixed(2),
                      shown ? '-> queue' : '-> stop');
        }

        if (shown) enqueue(card); else stop(card);
      });
      pump();
    }, { threshold: [0, PLAY_RATIO, 1] });

    cards.forEach(function (card) { player.observe(card); });

    function onVisibility() {
      if (document.hidden) {
        queue.length = 0;
        playing.slice().forEach(stop);
      } else {
        visible.forEach(enqueue);
        pump();
      }
    }
    document.addEventListener('visibilitychange', onVisibility);

    return {
      pauseAll: function () { queue.length = 0; playing.slice().forEach(stop); },
      resume: function () { visible.forEach(enqueue); pump(); },
      destroy: function () {
        loader.disconnect();
        player.disconnect();
        document.removeEventListener('visibilitychange', onVisibility);
        queue.length = 0;
        playing.slice().forEach(stop);
      }
    };
  }

  /* ====================== track navigation (arrows) ====================== */

  function initNav(root) {
    var track = root.querySelector('[data-vs-track]');
    var prev  = root.querySelector('[data-vs-prev]');
    var next  = root.querySelector('[data-vs-next]');
    if (!track || !prev || !next) return null;

    function step() {
      var item = track.querySelector('.video-shop__item');
      var w = item ? item.getBoundingClientRect().width + 16 : 256;
      return Math.max(w, Math.floor(track.clientWidth / w) * w || w);
    }

    var items = Array.prototype.slice.call(track.querySelectorAll('.video-shop__item'));

    /* Mobile is a centred peek carousel, so flag whichever card is nearest the
       middle. One read of the track box, then one pass — no thrash. */
    function markCentre() {
      if (!items.length) return;
      var box = track.getBoundingClientRect();
      var mid = box.left + box.width / 2;
      var best = null;
      var bestDist = Infinity;
      for (var i = 0; i < items.length; i++) {
        var r = items[i].getBoundingClientRect();
        var d = Math.abs((r.left + r.width / 2) - mid);
        if (d < bestDist) { bestDist = d; best = items[i]; }
      }
      items.forEach(function (el) { el.classList.toggle('is-center', el === best); });
    }

    function update() {
      var max = track.scrollWidth - track.clientWidth;
      var overflowing = max > 4;
      prev.hidden = !overflowing;
      next.hidden = !overflowing;
      if (overflowing) {
        var pos = Math.abs(track.scrollLeft);   // abs() also covers RTL
        prev.disabled = pos <= 2;
        next.disabled = pos >= max - 2;
      }
      markCentre();
    }

    function scrollByStep(dir) {
      var amount = step() * dir;
      if (typeof track.scrollBy === 'function') track.scrollBy({ left: amount, behavior: 'smooth' });
      else track.scrollLeft += amount;
    }

    prev.addEventListener('click', function () { scrollByStep(-1); });
    next.addEventListener('click', function () { scrollByStep(1); });

    var raf = null;
    function onScroll() {
      if (raf !== null) return;
      raf = requestAnimationFrame(function () { raf = null; update(); });
    }
    track.addEventListener('scroll', onScroll, { passive: true });

    var ro = null;
    if ('ResizeObserver' in window) { ro = new ResizeObserver(update); ro.observe(track); }
    window.addEventListener('resize', update, { passive: true });
    update();

    return {
      destroy: function () {
        track.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', update);
        if (ro) ro.disconnect();
      }
    };
  }

  /* ========================== likes / share ============================== */

  function initLikeShare(root) {
    function syncWidget(blockId, liked) {
      document
        .querySelectorAll('[data-widget-like-count][data-block-id="' + blockId + '"]')
        .forEach(function (el) {
          var base = parseInt(el.getAttribute('data-base-count'), 10) || 0;
          var text = el.querySelector('[data-widget-like-count-text]');
          if (text) text.textContent = base + (liked ? 1 : 0);
        });
    }

    root.querySelectorAll('[data-like-btn]').forEach(function (btn) {
      var blockId = btn.getAttribute('data-block-id');
      var liked = readLike(blockId);
      btn.setAttribute('aria-pressed', liked ? 'true' : 'false');
      syncWidget(blockId, liked);

      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        liked = !liked;
        writeLike(blockId, liked);
        document
          .querySelectorAll('[data-like-btn][data-block-id="' + blockId + '"]')
          .forEach(function (el) { el.setAttribute('aria-pressed', liked ? 'true' : 'false'); });
        syncWidget(blockId, liked);
        track('video_shop_like', { video_id: blockId, liked: liked });
      });
    });

    root.querySelectorAll('[data-share-btn]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var url = btn.getAttribute('data-share-url');
        var text = btn.getAttribute('data-share-text');
        var card = btn.closest('[data-video-card], [data-lightbox-slide]');
        track('video_shop_share', { video_id: card ? card.getAttribute('data-block-id') : '' });

        if (navigator.share) {
          navigator.share({ text: text || undefined, url: url }).catch(function () {});
          return;
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).catch(function () {});
        }
        window.open('https://wa.me/?text=' + encodeURIComponent(text ? text + ' ' + url : url),
                    '_blank', 'noopener');
      });
    });

    root.querySelectorAll('[data-track-btn="talk_to_us"]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var card = btn.closest('[data-video-card], [data-lightbox-slide]');
        track('video_shop_talk_to_us', { video_id: card ? card.getAttribute('data-block-id') : '' });
      });
    });

    root.querySelectorAll('[data-vs-atc]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        addToCart(btn);
      });
    });

    // Colour swatches in the mini-cart: pick a colour without leaving the reel.
    // Selecting one re-points the Add button, updates the thumbnail + price, and
    // marks the variant resolved when colour is the product's only option.
    root.querySelectorAll('[data-vs-swatches]').forEach(function (group) {
      var optionsCount = parseInt(group.getAttribute('data-vs-options-count'), 10) || 1;
      var buy = group.closest('.video-shop__buy');
      if (!buy) return;
      var atc = buy.querySelector('[data-vs-atc]');
      var headImg = buy.querySelector('.video-shop__buy-img');
      var priceEl = buy.querySelector('.video-shop__buy-price');
      var atcLabel = atc ? atc.querySelector('[data-atc-label]') : null;
      var addText = atcLabel ? atcLabel.textContent : '';
      var swatches = Array.prototype.slice.call(group.querySelectorAll('[data-vs-swatch]'));

      swatches.forEach(function (sw) {
        sw.addEventListener('click', function (e) {
          e.stopPropagation();
          swatches.forEach(function (o) {
            o.classList.remove('is-active');
            o.setAttribute('aria-pressed', 'false');
          });
          sw.classList.add('is-active');
          sw.setAttribute('aria-pressed', 'true');

          var price = sw.getAttribute('data-variant-price');
          var img = sw.getAttribute('data-variant-img');
          var available = sw.getAttribute('data-variant-available') === 'true';
          if (priceEl && price) priceEl.textContent = price;
          if (headImg && img) {
            headImg.removeAttribute('srcset');
            headImg.setAttribute('src', img);
          }

          if (!atc) return;
          atc.setAttribute('data-variant-id', sw.getAttribute('data-variant-id'));
          atc.setAttribute('data-product-url', sw.getAttribute('data-variant-url'));
          atc.setAttribute('data-complete', optionsCount <= 1 ? '1' : '0');
          atc.disabled = !available;
          if (atcLabel) atcLabel.textContent = available ? addText : 'Sold out';
        });
      });
    });
  }

  /* ============================= lightbox ================================ */

  function initLightbox(root, autoplay) {
    var lightbox = root.querySelector('[data-lightbox]');
    if (!lightbox) return null;

    var trackEl = lightbox.querySelector('[data-lightbox-track]');
    var slides = Array.prototype.slice.call(trackEl.querySelectorAll('[data-lightbox-slide]'));
    if (!slides.length) return null;

    var navPrev = lightbox.querySelector('[data-lb-prev]');
    var navNext = lightbox.querySelector('[data-lb-next]');

    var current = 0;
    var dragging = false;
    var startPos = 0;
    var delta = 0;
    var pointerId = null;
    var rafId = null;
    var seenPlay = {};
    var firstCtaTimer = null;   // delay before the "scroll up for next" nudge
    var firstCtaDone = false;   // true once the shopper has moved between videos

    var isNativeDialog = typeof lightbox.showModal === 'function';

    function horizontal() { return isDesktop(); }

    /* Desktop lays slides side by side and centres the active one, so the
       neighbours peek at either edge. Mobile is a full-height vertical reel. */
    function slideSize() {
      if (horizontal()) return slides[0].getBoundingClientRect().width || lightbox.clientWidth;
      return lightbox.clientHeight;
    }

    function centreOffset() {
      if (!horizontal()) return 0;
      return (lightbox.clientWidth - slideSize()) / 2;
    }

    function setTransform(shift, animate) {
      trackEl.style.transition = animate === false ? 'none' : 'transform 0.32s cubic-bezier(0.4,0,0.2,1)';
      trackEl.style.transform = horizontal()
        ? 'translate3d(' + shift + 'px,0,0)'
        : 'translate3d(0,' + shift + 'px,0)';
    }

    function position(index, drag) {
      return centreOffset() - index * slideSize() + (drag || 0);
    }

    function syncMuteIcon(slide) {
      var video = slide.querySelector('video');
      var btn = slide.querySelector('[data-lightbox-mute]');
      if (!video || !btn) return;
      var m = video.muted;
      var iconMuted = btn.querySelector('[data-icon-muted]');
      var iconUnmuted = btn.querySelector('[data-icon-unmuted]');
      if (iconMuted) iconMuted.style.display = m ? '' : 'none';
      if (iconUnmuted) iconUnmuted.style.display = m ? 'none' : '';
      btn.setAttribute('aria-pressed', m ? 'true' : 'false');
    }

    function pauseAll() {
      slides.forEach(function (s) {
        var v = s.querySelector('video');
        if (v) v.pause();
        // Leaving a slide resets its progress and hides its hint.
        var bar = s.querySelector('[data-vs-progress-bar]');
        if (bar) bar.style.width = '0%';
        var h = s.querySelector('[data-swipe-hint]');
        if (h) h.classList.remove('is-visible');
      });
    }

    /* Keep only the current clip and the NEXT one warm so a forward swipe starts
       instantly; release everything else — including the previous clip — so it
       stops buffering the moment you move on. Fewer live decoders also keeps the
       swipe smooth on mobile. A back-swipe reloads on demand. */
    function manageWindow(index) {
      slides.forEach(function (slide, i) {
        var video = slide.querySelector('video');
        if (!video) return;
        var d = i - index;               // signed: 0 = current, 1 = next
        if (d === 0 || d === 1) loadMedia(video);
        else unloadMedia(video);
      });
    }

    function markCurrent(index) {
      slides.forEach(function (s, i) { s.classList.toggle('is-current', i === index); });
      if (navPrev) navPrev.disabled = index <= 0;
      if (navNext) navNext.disabled = index >= slides.length - 1;
    }

    /* First-run nudge: a few seconds into the opening video, surface a tappable
       "scroll up for next video" cue. The reel no longer auto-advances, so new
       shoppers need to be shown the gesture exists. It lives on the first slide
       only, shows once per open, and clears the moment they move between videos. */
    function firstCta() {
      return slides[0] && slides[0].querySelector('[data-vs-next-cta]');
    }

    function hideFirstCta() {
      if (firstCtaTimer) { clearTimeout(firstCtaTimer); firstCtaTimer = null; }
      var cta = firstCta();
      if (cta) cta.classList.remove('is-visible');
    }

    function armFirstCta() {
      var cta = firstCta();
      if (!cta || slides.length < 2) return;   // nothing to advance to
      firstCtaTimer = setTimeout(function () {
        firstCtaTimer = null;
        if (current === 0 && !firstCtaDone) cta.classList.add('is-visible');
      }, 5000);
    }

    function goTo(index, opts) {
      opts = opts || {};
      index = Math.max(0, Math.min(slides.length - 1, index));
      var changed = index !== current || opts.force === true;
      current = index;

      setTransform(position(index), opts.animate);
      markCurrent(index);

      var slide = slides[index];
      var video = slide.querySelector('video');
      if (!video) return;

      // A drag that snaps back lands on the same slide — leave it playing.
      if (!changed && !video.paused) return;

      pauseAll();
      manageWindow(index);
      loadMedia(video);
      var stageEl = slide.querySelector('.video-shop__stage');
      if (stageEl) stageEl.classList.remove('is-paused');   // no stale play icon on arrival
      video.currentTime = 0;
      // Opening is a user gesture, so sound is allowed; attemptPlay falls back
      // to muted if the browser still refuses. The icon is synced either way.
      video.muted = false;
      attemptPlay(video, function () { syncMuteIcon(slide); });
      syncMuteIcon(slide);

      var id = slide.getAttribute('data-block-id');
      if (!seenPlay[id]) {
        seenPlay[id] = true;
        track('video_shop_play', { video_id: id });
      }

      // Arm the first-video nudge on the opening slide; drop it once they move on.
      hideFirstCta();
      if (index === 0) {
        if (!firstCtaDone) armFirstCta();
      } else {
        firstCtaDone = true;
      }
    }

    function open(blockId) {
      var idx = 0;
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('data-block-id') === blockId) { idx = i; break; }
      }
      if (autoplay && autoplay.pauseAll) autoplay.pauseAll();

      // Fresh open: let the first-video nudge show again (unless they open
      // straight onto a later video, where goTo marks it done).
      firstCtaDone = false;
      hideFirstCta();

      if (isNativeDialog) {
        if (!lightbox.open) lightbox.showModal();
      } else {
        lightbox.setAttribute('open', '');
        lightbox.classList.add('is-open');
        document.documentElement.style.overflow = 'hidden';
      }
      // Wait a frame so the dialog has real dimensions before measuring.
      requestAnimationFrame(function () {
        goTo(idx, { animate: false, force: true });
      });
    }

    function close() {
      if (isNativeDialog) {
        if (lightbox.open) lightbox.close();
      } else {
        lightbox.removeAttribute('open');
        lightbox.classList.remove('is-open');
        document.documentElement.style.overflow = '';
      }
      hideFirstCta();
      pauseAll();
      slides.forEach(function (s) { unloadMedia(s.querySelector('video')); });
      if (autoplay && autoplay.resume) autoplay.resume();
    }

    lightbox.addEventListener('close', function () {
      hideFirstCta();
      pauseAll();
      slides.forEach(function (s) { unloadMedia(s.querySelector('video')); });
      if (autoplay && autoplay.resume) autoplay.resume();
    });

    /* ------------------------------ openers ----------------------------- */

    root.querySelectorAll('[data-open-lightbox]').forEach(function (media) {
      media.addEventListener('click', function () { open(media.getAttribute('data-block-id')); });
      media.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        open(media.getAttribute('data-block-id'));
      });
    });

    // Scope the widget to this section, so two Video Shop sections on one page
    // don't both bind to the same button.
    var sectionId = root.getAttribute('data-section-id');
    var widget = document.querySelector('[data-floating-widget][data-section-id="' + sectionId + '"]');
    if (widget) {
      var widgetVideo = widget.querySelector('[data-vs-video]');
      if (widgetVideo && !saveData()) {
        loadMedia(widgetVideo);
        widgetVideo.muted = true;
        safePlay(widgetVideo);
      }
      widget.addEventListener('click', function () {
        track('video_shop_widget_click', { video_id: widget.getAttribute('data-block-id') });
        open(widget.getAttribute('data-block-id'));
      });
    }

    // Close buttons live per-slide, so bind every one.
    lightbox.querySelectorAll('[data-lightbox-close]').forEach(function (btn) {
      btn.addEventListener('click', function (e) { e.stopPropagation(); close(); });
    });

    if (navPrev) navPrev.addEventListener('click', function () { goTo(current - 1); });
    if (navNext) navNext.addEventListener('click', function () { goTo(current + 1); });

    /* --------------------------- per-slide UI --------------------------- */

    slides.forEach(function (slide, index) {
      slide.addEventListener('click', function (e) {
        if (dragging) return;
        if (e.target.closest('.video-shop__rail') ||
            e.target.closest('.video-shop__stage-top') ||
            e.target.closest('.video-shop__buy') ||
            e.target.closest('.video-shop__next-cta')) return;
        // Clicking a neighbour brings it forward rather than toggling play.
        if (index !== current) { goTo(index); return; }
        var video = slide.querySelector('video');
        if (!video) return;
        if (video.paused) safePlay(video); else video.pause();
      });

      var muteBtn = slide.querySelector('[data-lightbox-mute]');
      if (muteBtn) {
        muteBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          var video = slide.querySelector('video');
          if (!video) return;
          video.muted = !video.muted;
          syncMuteIcon(slide);
        });
      }

      var video = slide.querySelector('video');
      if (!video) return;

      var hint = slide.querySelector('[data-swipe-hint]');
      var bar = slide.querySelector('[data-vs-progress-bar]');
      var stage = slide.querySelector('.video-shop__stage');

      // Reflect play/pause so the centre play icon shows while paused.
      video.addEventListener('play', function () { if (stage) stage.classList.remove('is-paused'); });
      video.addEventListener('playing', function () { if (stage) stage.classList.remove('is-paused'); });
      video.addEventListener('pause', function () { if (stage) stage.classList.add('is-paused'); });

      // Only the first slide carries the nudge; tapping it advances the reel.
      var nextCta = slide.querySelector('[data-vs-next-cta]');
      if (nextCta) {
        nextCta.addEventListener('click', function (e) {
          e.stopPropagation();
          firstCtaDone = true;
          hideFirstCta();
          goTo(current + 1 >= slides.length ? 0 : current + 1);
        });
      }

      video.addEventListener('timeupdate', function () {
        if (!video.duration || !isFinite(video.duration)) return;
        if (bar) bar.style.width = ((video.currentTime / video.duration) * 100) + '%';
        // Surface the swipe affordance as the clip runs out. The first slide
        // shows its own "scroll up" nudge instead, so skip the generic hint there.
        if (hint && index !== 0) hint.classList.toggle('is-visible', video.duration - video.currentTime <= 3);
      });

      // Manual navigation only: the reel never auto-advances. When the clip
      // finishes, replay it in place so it keeps looping like a reel; the
      // shopper moves on by swiping (mobile) or the arrows (desktop).
      video.addEventListener('ended', function () {
        track('video_shop_complete', { video_id: slide.getAttribute('data-block-id') });
        if (bar) bar.style.width = '0%';
        if (stage) stage.classList.remove('is-paused');   // looping, not a pause
        video.currentTime = 0;
        safePlay(video);
      });
    });

    /* ------------------------------- drag ------------------------------- */

    function onMove() {
      rafId = null;
      setTransform(position(current, delta), false);
    }

    trackEl.addEventListener('pointerdown', function (e) {
      if (e.target.closest('.video-shop__rail') ||
          e.target.closest('.video-shop__stage-top') ||
          e.target.closest('.video-shop__buy') ||
          e.target.closest('.video-shop__next-cta')) return;
      dragging = true;
      pointerId = e.pointerId;
      startPos = horizontal() ? e.clientX : e.clientY;
      delta = 0;
      trackEl.style.transition = 'none';
      try { trackEl.setPointerCapture(e.pointerId); } catch (err) {}
    });

    trackEl.addEventListener('pointermove', function (e) {
      if (!dragging || e.pointerId !== pointerId) return;
      delta = (horizontal() ? e.clientX : e.clientY) - startPos;
      if (rafId === null) rafId = requestAnimationFrame(onMove);
    }, { passive: true });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      pointerId = null;
      if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
      var threshold = slideSize() * 0.16;
      if (delta < -threshold) goTo(current + 1);
      else if (delta > threshold) goTo(current - 1);
      // A tap or a drag too small to flip slides just snaps the track back.
      // Crucially it does NOT call goTo(current): that would seek the current
      // video to 0 and replay it, so a tap-to-pause would restart from the top.
      else setTransform(position(current), true);
      delta = 0;
    }

    trackEl.addEventListener('pointerup', endDrag);
    trackEl.addEventListener('pointercancel', endDrag);

    /* ---------------------------- keyboard ------------------------------ */

    function isOpen() {
      return isNativeDialog ? lightbox.open : lightbox.classList.contains('is-open');
    }

    function onKey(e) {
      if (!isOpen()) return;
      if (e.key === 'Escape' && !isNativeDialog) close();  // <dialog> handles Esc itself
      var back = horizontal() ? 'ArrowLeft' : 'ArrowUp';
      var fwd  = horizontal() ? 'ArrowRight' : 'ArrowDown';
      if (e.key === back) { e.preventDefault(); goTo(current - 1); }
      if (e.key === fwd)  { e.preventDefault(); goTo(current + 1); }
    }
    document.addEventListener('keydown', onKey);

    var resizeTimer;
    function onResize() {
      if (!isOpen()) return;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { goTo(current, { animate: false }); }, 120);
    }
    window.addEventListener('resize', onResize, { passive: true });

    /* ----------------------------- deep link ---------------------------- */

    if (window.location.hash.indexOf('#video-') === 0) {
      var wanted = window.location.hash.slice('#video-'.length);
      var exists = slides.some(function (s) { return s.getAttribute('data-block-id') === wanted; });
      if (exists) requestAnimationFrame(function () { open(wanted); });
    }

    return {
      close: close,
      destroy: function () {
        document.removeEventListener('keydown', onKey);
        window.removeEventListener('resize', onResize);
        close();
      }
    };
  }

  /* ============================== bootstrap ============================== */

  var instances = new WeakMap();

  function initSection(root) {
    if (instances.has(root)) return;
    var autoplay = initAutoplay(root);
    var nav = initNav(root);
    var lightbox = initLightbox(root, autoplay);
    instances.set(root, { autoplay: autoplay, nav: nav, lightbox: lightbox });
    initLikeShare(root);   // after instances.set, so ATC can find the lightbox
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-vs-root]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  /* Theme editor: sections are swapped in and out live. */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-vs-root]');
    if (!root) return;
    var inst = instances.get(root);
    if (!inst) return;
    if (inst.autoplay && inst.autoplay.destroy) inst.autoplay.destroy();
    if (inst.nav && inst.nav.destroy) inst.nav.destroy();
    if (inst.lightbox && inst.lightbox.destroy) inst.lightbox.destroy();
    instances.delete(root);
  });
})();
